import invariant from 'invariant'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'

import { login, loginFailure } from '../actions'
import { useApi } from '../api'
import apiTypes from '../apiTypes'

import { getAvailableOrganization } from '../utils'
import useCallbackWhenRequestFulfills from './useCallbackWhenRequestFulfills'
import useSession from './useSession'
import {
  LoggedInSessionStateT,
  SessionStateT,
  SystemConfiguration,
} from '../../types'
import { Nullable } from '@signavio/effektif-commons'

type Props = {
  configuration: SystemConfiguration
  loginSession?: LoggedInSessionStateT
  redirectTo?: string
  redirectToPath?: string
}

type ReturnType = [
  session: SessionStateT,
  systemConfiguration: Nullable<SystemConfiguration>
]

function useUniversalLogin(organizationKey?: string): ReturnType {
  const dispatch = useDispatch()
  const history = useHistory()
  const session = useSession()

  const [systemConfiguration, setSystemConfiguration] =
    useState<Nullable<SystemConfiguration>>(null)

  const [authRequest, authenticate] = useApi(apiTypes.AUTH, {
    method: 'create',
  })

  useEffect(() => {
    authenticate()
  }, [authenticate])

  useCallbackWhenRequestFulfills(
    authRequest,
    ({ configuration, redirectTo, loginSession, redirectToPath }: Props) => {
      setSystemConfiguration(configuration)

      if (redirectTo) {
        window.location.href = redirectTo

        return
      }

      if (!loginSession) {
        dispatch(loginFailure())

        return
      }

      const { organizations, user, ...session } = loginSession
      const organization = getAvailableOrganization(
        organizations,
        organizationKey
      )

      invariant(organization, 'All user organizations are disabled')

      dispatch(
        login({
          ...session,
          user,
          organization,
          organizations,
        })
      )

      if (redirectToPath) {
        history.replace(redirectToPath)
      }
    }
  )

  return [session, systemConfiguration]
}

export default useUniversalLogin
